import React, { useState, useEffect } from "react";
import { Box, Container, Typography, Link, List, ListItem, ListItemText, Divider } from "@mui/material";
import Homenavbar from "./Homenavbar";
import Homefooter from "./Homefooter";
import DemoRequestDialog from "./Components/DemoRequestDialog";

const PrivacyPolicy = () => {
    const [demoDialogOpen, setDemoDialogOpen] = useState(false);
    const [activeLink, setActiveLink] = useState('');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleDemoOpen = () => setDemoDialogOpen(true);
    const handleDemoClose = () => setDemoDialogOpen(false);

    return (
        <Box sx={{ backgroundColor: "#F8FAFC", minHeight: "100vh" }}>
            <Homenavbar
                onRequestDemo={handleDemoOpen}
                activeLink={activeLink}
                setActiveLink={setActiveLink}
            />
            <Container maxWidth="lg" sx={{ py: 12 }}>
                <Box sx={{ backgroundColor: "#FFFFFF", p: { xs: 4, md: 8 }, borderRadius: "20px", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: "#1E293B", mb: 2, textAlign: "center" }}>
                        Privacy Policy
                    </Typography>
                    <Typography variant="subtitle1" sx={{ color: "#64748B", textAlign: "center", mb: 6 }}>
                        (Updated Date: 24-01-2025)
                    </Typography>

                    <Typography paragraph sx={{ color: "#475569", mb: 4, fontSize: "1.1rem" }}>
                        At <strong>jobsnprofiles.com</strong>, we value your privacy and are committed to protecting your personal information. This Privacy Policy outlines how we collect, use, disclose, and safeguard your data when you use our platform.
                    </Typography>

                    <Box component="section" sx={{ mb: 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: "#1E293B", mb: 2 }}>
                            1. Information We Collect
                        </Typography>
                        <Typography sx={{ color: "#475569", mb: 2 }}>We collect the following types of information:</Typography>

                        <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: 600, color: "#334155", mt: 2, mb: 1 }}>
                            a. Personal Information
                        </Typography>
                        <List dense>
                            {["Name", "Email address", "Contact number", "Job title and professional details", "Resume or CV"].map((item) => (
                                <ListItem key={item} disablePadding>
                                    <ListItemText primary={`• ${item}`} primaryTypographyProps={{ style: { color: "#475569" } }} />
                                </ListItem>
                            ))}
                        </List>

                        <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: 600, color: "#334155", mt: 2, mb: 1 }}>
                            b. Non-Personal Information
                        </Typography>
                        <List dense>
                            {["Browser type and version", "Operating system", "IP address", "Usage data, such as pages visited, and time spent on the site"].map((item) => (
                                <ListItem key={item} disablePadding>
                                    <ListItemText primary={`• ${item}`} primaryTypographyProps={{ style: { color: "#475569" } }} />
                                </ListItem>
                            ))}
                        </List>

                        <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: 600, color: "#334155", mt: 2, mb: 1 }}>
                            c. Information from Third-Party Sources
                        </Typography>
                        <List dense>
                            <ListItem disablePadding>
                                <ListItemText primary="• LinkedIn profile data (when using our Chrome extension)" primaryTypographyProps={{ style: { color: "#475569" } }} />
                            </ListItem>
                        </List>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    <Box component="section" sx={{ mb: 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: "#1E293B", mb: 2 }}>
                            2. How We Use Your Information
                        </Typography>
                        <Typography sx={{ color: "#475569", mb: 2 }}>We use your information for:</Typography>
                        <List dense>
                            {[
                                "Facilitating job matches between employers and candidates.",
                                "Enhancing your experience on our platform.",
                                "Sending notifications and updates about job fairs, new features, or other relevant services.",
                                "Analyzing site usage to improve functionality and performance.",
                                "Ensuring compliance with legal and regulatory requirements."
                            ].map((item) => (
                                <ListItem key={item} disablePadding>
                                    <ListItemText primary={`• ${item}`} primaryTypographyProps={{ style: { color: "#475569" } }} />
                                </ListItem>
                            ))}
                        </List>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    <Box component="section" sx={{ mb: 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: "#1E293B", mb: 2 }}>
                            3. Sharing Your Information
                        </Typography>
                        <Typography sx={{ color: "#475569", mb: 2 }}>
                            We do not sell your personal information. However, we may share your data with:
                        </Typography>
                        <List dense>
                            <ListItem disablePadding sx={{ mb: 1 }}>
                                <ListItemText
                                    primary={<span><strong>Employers and Recruiters:</strong> For job applications and candidate matching.</span>}
                                    primaryTypographyProps={{ style: { color: "#475569" } }}
                                />
                            </ListItem>
                            <ListItem disablePadding sx={{ mb: 1 }}>
                                <ListItemText
                                    primary={<span><strong>Service Providers:</strong> Third-party vendors assisting us with platform functionality, analytics, and communication.</span>}
                                    primaryTypographyProps={{ style: { color: "#475569" } }}
                                />
                            </ListItem>
                            <ListItem disablePadding>
                                <ListItemText
                                    primary={<span><strong>Legal Authorities:</strong> when authorized by law or to safeguard our users and our rights.</span>}
                                    primaryTypographyProps={{ style: { color: "#475569" } }}
                                />
                            </ListItem>
                        </List>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    <Box component="section" sx={{ mb: 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: "#1E293B", mb: 2 }}>
                            4. Chrome Extension
                        </Typography>
                        <Typography sx={{ color: "#475569", mb: 2 }}>
                            Our Chrome extension is designed to assist employers by enabling them to extract and organize professional profile data from LinkedIn. The following points outline how the extension operates and handles data:
                        </Typography>
                        <List dense>
                            {[
                                "The extension collects publicly available information from LinkedIn profiles to help employers match candidates to job postings on jobsnprofiles.com.",
                                "Only employers registered on our platform and with active job postings can use the extension.",
                                "Data extracted using the extension is securely transmitted to our platform for processing and matching purposes.",
                                "The extension does not store or share extracted data outside of jobsnprofiles.com.",
                                "We comply with LinkedIn's policies and guidelines to ensure ethical data handling and usage."
                            ].map((item) => (
                                <ListItem key={item} disablePadding>
                                    <ListItemText primary={`• ${item}`} primaryTypographyProps={{ style: { color: "#475569" } }} />
                                </ListItem>
                            ))}
                        </List>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    <Box component="section" sx={{ mb: 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: "#1E293B", mb: 2 }}>
                            5. Cookies and Tracking Technologies
                        </Typography>
                        <Typography sx={{ color: "#475569", mb: 2 }}>We use cookies and similar technologies to:</Typography>
                        <List dense>
                            {["Enhance your browsing experience.", "Understand user preferences and interactions.", "Deliver targeted advertisements."].map((item) => (
                                <ListItem key={item} disablePadding>
                                    <ListItemText primary={`• ${item}`} primaryTypographyProps={{ style: { color: "#475569" } }} />
                                </ListItem>
                            ))}
                        </List>
                        <Typography sx={{ color: "#475569", mt: 2 }}>
                            Through your browser's options, you may modify your cookie choices.
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    <Box component="section" sx={{ mb: 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: "#1E293B", mb: 2 }}>
                            6. Data Security
                        </Typography>
                        <Typography sx={{ color: "#475569" }}>
                            We implement appropriate technical and organizational measures to protect your data from unauthorized access, alteration, or loss.
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    <Box component="section" sx={{ mb: 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: "#1E293B", mb: 2 }}>
                            7. Your Rights
                        </Typography>
                        <Typography sx={{ color: "#475569", mb: 2 }}>You have the right to:</Typography>
                        <List dense>
                            {[
                                "Access and request a copy of your data.",
                                "Update or correct inaccuracies in your personal information.",
                                "Delete your account and associated data.",
                                "Opt-out of marketing communications."
                            ].map((item) => (
                                <ListItem key={item} disablePadding>
                                    <ListItemText primary={`• ${item}`} primaryTypographyProps={{ style: { color: "#475569" } }} />
                                </ListItem>
                            ))}
                        </List>
                        <Typography sx={{ color: "#475569", mt: 2 }}>
                            To exercise your rights, contact us at <Link href="mailto:contact@jobsnprofiles.com" sx={{ color: "#1976D2" }}>contact@jobsnprofiles.com</Link>.
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    <Box component="section" sx={{ mb: 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: "#1E293B", mb: 2 }}>
                            8. Third-Party Links
                        </Typography>
                        <Typography sx={{ color: "#475569" }}>
                            Our platform may contain links to third-party websites. We have no control over these websites' content or privacy policies. Please review their privacy policies independently.
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    <Box component="section" sx={{ mb: 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: "#1E293B", mb: 2 }}>
                            9. Changes to This Privacy Policy
                        </Typography>
                        <Typography sx={{ color: "#475569" }}>
                            We may make changes to our privacy statement. The revised policy will be posted with the "Effective Date" updated accordingly. Please check this website from time to time.
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    <Box component="section">
                        <Typography variant="h5" sx={{ fontWeight: 600, color: "#1E293B", mb: 2 }}>
                            10. Contact Us
                        </Typography>
                        <Typography sx={{ color: "#475569", mb: 1 }}>
                            If you have any questions or concerns about this Privacy Policy, please get in touch with us at:
                        </Typography>
                        <Typography sx={{ color: "#475569" }}>
                            <strong>Email:</strong> <Link href="mailto:contact@jobsnprofiles.com" sx={{ color: "#1976D2" }}>contact@jobsnprofiles.com</Link>
                        </Typography>
                        <Typography sx={{ color: "#475569" }}>
                            <strong>Phone:</strong> +1 510 320 1346
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 6, textAlign: "center" }}>
                        <Typography variant="h6" sx={{ color: "#64748B", fontWeight: 500 }}>
                            Thank you for trusting jobsnprofiles.com to assist with your job search and recruitment needs.
                        </Typography>
                    </Box>
                </Box>
            </Container>
            <Homefooter onRequestDemo={handleDemoOpen} />
            <DemoRequestDialog open={demoDialogOpen} onClose={handleDemoClose} />
        </Box>
    );
};

export default PrivacyPolicy;
